<template>
  <div class="modal fade" id="reconciliationModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header bg-success text-white">
          <h5 class="modal-title">
            <i class="bx bx-check-circle me-2"></i>Reconcile Cash Collection
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading submission details...</p>
          </div>

          <div v-else-if="reportData">
            <!-- Submission Summary -->
            <div class="row mb-4">
              <div class="col-md-8">
                <div class="card border-info">
                  <div class="card-header bg-info text-white">
                    <h6 class="card-title mb-0">
                      <i class="bx bx-file-blank me-2"></i>Submission Details
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6">
                        <p><strong>Report Reference:</strong> {{ reportData.report_reference }}</p>
                        <p><strong>Collector:</strong> {{ reportData.collector_name }}</p>
                        <p><strong>Collection Date:</strong> {{ formatDate(reportData.collection_date) }}</p>
                        <p><strong>Collection Point:</strong> {{ reportData.collection_point }}</p>
                      </div>
                      <div class="col-md-6">
                        <p><strong>Submission Date:</strong> {{ formatDateTime(reportData.submission_date) }}</p>
                        <p><strong>Bank Reference:</strong> {{ reportData.bank_deposit_reference || '-' }}</p>
                        <p><strong>Total Transactions:</strong> {{ reportData.total_transactions }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <div class="card border-primary">
                  <div class="card-header bg-primary text-white">
                    <h6 class="card-title mb-0">
                      <i class="bx bx-calculator me-2"></i>Amount Summary
                    </h6>
                  </div>
                  <div class="card-body text-center">
                    <div class="mb-3">
                      <h4 class="text-primary">ETB {{ formatCurrency(reportData.total_amount_collected) }}</h4>
                      <small class="text-muted">Expected Amount</small>
                    </div>
                    <div class="mb-3">
                      <h4 class="text-info">ETB {{ formatCurrency(reportData.amount_submitted) }}</h4>
                      <small class="text-muted">Submitted Amount</small>
                    </div>
                    <div class="border-top pt-3">
                      <h5 :class="varianceClass">
                        {{ reportData.variance_amount >= 0 ? '+' : '' }}ETB {{ formatCurrency(Math.abs(reportData.variance_amount)) }}
                      </h5>
                      <small class="text-muted">{{ varianceText }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Transaction Details -->
            <div class="card mb-4">
              <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                  <h6 class="card-title mb-0">
                    <i class="bx bx-list-ul me-2"></i>Transaction Details
                  </h6>
                  <span class="badge bg-primary">{{ transactions.length }} Transactions</span>
                </div>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-hover table-sm">
                    <thead class="table-light">
                    <tr>
                      <th width="5%">#</th>
                      <th>Member</th>
                      <th>CBHI Number</th>
                      <th>Receipt</th>
                      <th>Method</th>
                      <th>Time</th>
                      <th class="text-end">Amount</th>
                      <th class="text-center">Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(transaction, index) in transactions" :key="transaction.payment_id">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <strong>{{ transaction.member_name }}</strong>
                      </td>
                      <td>{{ transaction.cbhi_number }}</td>
                      <td>{{ transaction.receipt_number }}</td>
                      <td>
                        <i :class="getPaymentMethodIcon(transaction.payment_method)" class="me-1"></i>
                        {{ transaction.payment_method }}
                      </td>
                      <td>{{ formatTime(transaction.collection_time) }}</td>
                      <td class="text-end">
                        <strong>ETB {{ formatCurrency(transaction.payment_amount) }}</strong>
                      </td>
                      <td class="text-center">
                          <span class="badge bg-success" v-if="transaction.included_in_submission">
                            <i class="bx bx-check me-1"></i>Included
                          </span>
                        <span class="badge bg-warning" v-else>
                            <i class="bx bx-x me-1"></i>Excluded
                          </span>
                      </td>
                    </tr>
                    </tbody>
                    <tfoot class="table-light">
                    <tr>
                      <td colspan="6"><strong>Total Amount</strong></td>
                      <td class="text-end">
                        <strong class="text-primary">ETB {{ formatCurrency(totalTransactionAmount) }}</strong>
                      </td>
                      <td></td>
                    </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <!-- Reconciliation Form -->
            <form @submit.prevent="reconcileCollection">
              <div class="card border-success">
                <div class="card-header bg-success text-white">
                  <h6 class="card-title mb-0">
                    <i class="bx bx-shield-check me-2"></i>Reconciliation Verification
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">
                        <strong>Bank Verification Status <span class="text-danger">*</span></strong>
                      </label>
                      <select v-model="reconciliationForm.bankVerificationStatus" class="form-select" required>
                        <option value="">Select verification status</option>
                        <option value="verified">Verified - Amount matches bank deposit</option>
                        <option value="partial">Partial - Amount partially verified</option>
                        <option value="disputed">Disputed - Amount does not match</option>
                      </select>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label class="form-label">
                        <strong>Reconciliation Date <span class="text-danger">*</span></strong>
                      </label>
                      <input
                          type="datetime-local"
                          v-model="reconciliationForm.reconciliationDate"
                          class="form-control"
                          required
                      >
                    </div>

                    <div class="col-md-6 mb-3" v-if="Math.abs(reportData.variance_amount) > 0">
                      <label class="form-label">
                        <strong>Variance Resolution <span class="text-danger">*</span></strong>
                      </label>
                      <select v-model="reconciliationForm.varianceResolution" class="form-select" required>
                        <option value="">Select resolution</option>
                        <option value="approved">Approved - Variance accepted</option>
                        <option value="adjusted">Adjusted - Amount corrected</option>
                        <option value="pending_investigation">Pending Investigation</option>
                      </select>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label class="form-label">
                        <strong>Final Status <span class="text-danger">*</span></strong>
                      </label>
                      <select v-model="reconciliationForm.finalStatus" class="form-select" required>
                        <option value="">Select final status</option>
                        <option value="reconciled">Reconciled - Completed successfully</option>
                        <option value="disputed">Disputed - Requires further review</option>
                      </select>
                    </div>

                    <div class="col-12 mb-3">
                      <label class="form-label">
                        <strong>Reconciliation Notes <span class="text-danger">*</span></strong>
                      </label>
                      <textarea
                          v-model="reconciliationForm.notes"
                          class="form-control"
                          rows="4"
                          placeholder="Enter detailed reconciliation notes, including any discrepancies found, actions taken, and final resolution..."
                          required
                      ></textarea>
                    </div>

                    <!-- Additional Information for Disputed Cases -->
                    <div v-if="reconciliationForm.finalStatus === 'disputed'" class="col-12 mb-3">
                      <div class="alert alert-warning">
                        <h6 class="alert-heading">
                          <i class="bx bx-exclamation-triangle me-2"></i>Disputed Collection
                        </h6>
                        <div class="row">
                          <div class="col-md-6 mb-3">
                            <label class="form-label">Next Review Date</label>
                            <input
                                type="date"
                                v-model="reconciliationForm.nextReviewDate"
                                class="form-control"
                            >
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label">Assigned Reviewer</label>
                            <select v-model="reconciliationForm.assignedReviewer" class="form-select">
                              <option value="">Select reviewer</option>
                              <option v-for="reviewer in reviewers" :key="reviewer.id" :value="reviewer.id">
                                {{ reviewer.name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-12">
                            <label class="form-label">Dispute Reason</label>
                            <textarea
                                v-model="reconciliationForm.disputeReason"
                                class="form-control"
                                rows="3"
                                placeholder="Detailed reason for the dispute..."
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                  <i class="bx bx-x me-1"></i>Cancel
                </button>
                <button type="submit" class="btn btn-success" :disabled="reconciling || !isFormValid">
                  <span v-if="reconciling">
                    <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                    Processing...
                  </span>
                  <span v-else>
                    <i class="bx bx-check-circle me-1"></i>Complete Reconciliation
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, executeJson } from "@/api";

export default {
  name: "ReconciliationModal",
  data() {
    return {
      modalInstance: null,
      loading: false,
      reconciling: false,
      reportData: null,
      transactions: [],
      reviewers: [],
      reconciliationForm: {
        bankVerificationStatus: '',
        reconciliationDate: '',
        varianceResolution: '',
        finalStatus: '',
        notes: '',
        nextReviewDate: '',
        assignedReviewer: '',
        disputeReason: ''
      }
    };
  },

  computed: {
    varianceClass() {
      if (!this.reportData) return 'text-muted';
      if (Math.abs(this.reportData.variance_amount) < 0.01) return 'text-success';
      return this.reportData.variance_amount > 0 ? 'text-warning' : 'text-danger';
    },

    varianceText() {
      if (!this.reportData) return '';
      if (Math.abs(this.reportData.variance_amount) < 0.01) return 'Exact Match';
      return this.reportData.variance_amount > 0 ? 'Overage' : 'Shortage';
    },

    totalTransactionAmount() {
      return this.transactions
          .filter(t => t.included_in_submission)
          .reduce((sum, t) => sum + parseFloat(t.payment_amount || 0), 0);
    },

    isFormValid() {
      const basic = this.reconciliationForm.bankVerificationStatus &&
          this.reconciliationForm.reconciliationDate &&
          this.reconciliationForm.finalStatus &&
          this.reconciliationForm.notes.trim();

      if (!basic) return false;

      // Additional validation for variance resolution
      if (Math.abs(this.reportData?.variance_amount || 0) > 0 && !this.reconciliationForm.varianceResolution) {
        return false;
      }

      // Additional validation for disputed cases
      if (this.reconciliationForm.finalStatus === 'disputed' && !this.reconciliationForm.disputeReason.trim()) {
        return false;
      }

      return true;
    }
  },

  mounted() {
    if (typeof window !== 'undefined' && window.bootstrap) {
      this.modalInstance = new window.bootstrap.Modal(document.getElementById('reconciliationModal'));
    }
    this.initializeForm();
    this.loadReviewers();
  },

  methods: {
    async openModal(report) {
      this.loading = true;
      this.reportData = report;

      if (this.modalInstance) {
        this.modalInstance.show();
      }

      try {
        await this.loadReportDetails();
      } catch (error) {
        console.error('Error loading report details:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadReportDetails() {
      const data = {
        "function": "getCashCollectionReportDetails",
        "reportId": this.reportData.report_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.transactions = res.data.data.transactions || [];
        }
      } catch (e) {
        console.error("Failed to load report details:", e);
      }
    },

    async loadReviewers() {
      try {
        const data = { "function": "getFinanceReviewers" };
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.reviewers = res.data.data.reviewers || [];
        }
      } catch (e) {
        console.error("Failed to load reviewers:", e);
      }
    },

    initializeForm() {
      const now = new Date();
      this.reconciliationForm = {
        bankVerificationStatus: '',
        reconciliationDate: now.toISOString().slice(0, 16),
        varianceResolution: '',
        finalStatus: '',
        notes: '',
        nextReviewDate: '',
        assignedReviewer: '',
        disputeReason: ''
      };
    },

    async reconcileCollection() {
      if (!this.isFormValid) return;

      this.reconciling = true;

      const data = {
        "function": "reconcileCashCollection",
        "reportId": this.reportData.report_id,
        "bankVerificationStatus": this.reconciliationForm.bankVerificationStatus,
        "reconciliationDate": this.reconciliationForm.reconciliationDate,
        "varianceResolution": this.reconciliationForm.varianceResolution,
        "finalStatus": this.reconciliationForm.finalStatus,
        "notes": this.reconciliationForm.notes,
        "nextReviewDate": this.reconciliationForm.nextReviewDate,
        "assignedReviewer": this.reconciliationForm.assignedReviewer,
        "disputeReason": this.reconciliationForm.disputeReason,
        "reconciledBy": localStorage.getItem("userId") || 1,
        "reconciledByName": localStorage.getItem("userName") || "Admin"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('collection-reconciled', {
            message: 'Cash collection reconciled successfully',
            reportId: this.reportData.report_id,
            status: this.reconciliationForm.finalStatus
          });

          if (this.modalInstance) {
            this.modalInstance.hide();
          }
          this.resetForm();
        } else {
          this.showError(res.data.message || 'Failed to reconcile collection');
        }
      } catch (e) {
        this.showError('Error reconciling cash collection');
      } finally {
        this.reconciling = false;
      }
    },

    getPaymentMethodIcon(method) {
      const methodLower = method.toLowerCase();
      if (methodLower.includes('cash')) return 'bx bx-money text-warning';
      if (methodLower.includes('telebirr') || methodLower.includes('mpesa') || methodLower.includes('cbe')) {
        return 'bx bx-mobile-alt text-success';
      }
      if (methodLower.includes('bank')) return 'bx bx-building-house text-primary';
      return 'bx bx-credit-card text-info';
    },

    resetForm() {
      this.initializeForm();
      this.reportData = null;
      this.transactions = [];
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    showError(message) {
      // You can implement a toast notification or alert here
      alert(message);
    }
  }
};
</script>

<style scoped>
.btn-close-white {
  filter: brightness(0) invert(1);
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.modal-body {
  max-height: 80vh;
  overflow-y: auto;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.badge {
  font-size: 0.75rem;
  padding: 0.35rem 0.65rem;
}

.alert-heading {
  margin-bottom: 0.5rem;
}
</style>